import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation } from "@tanstack/react-query";
import { Chat } from "@/types/chat";
import { useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

interface GenerateFlowPayload {
  key: string;
  uuid: string;
  documentId?: number;
}

interface UseGenerateFlowServiceResult {
  mutate: (payload: GenerateFlowPayload) => void;
  abort: () => void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  data: Chat | undefined;
}

/**
 * Generate flow API call
 * @param payload - The payload including auth token
 * @param signal - AbortSignal to cancel the request
 * @returns Promise<Chat> - Generated chat/flow response
 */
export async function generateFlow(
  payload: GenerateFlowPayload & { token: string },
  signal?: AbortSignal
): Promise<Chat> {
  const { token, ...flowData } = payload;
  const url = `${config.isometricApiUrl}/chat/generate`;

  try {
    const response = await apiFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(flowData),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to generate flow: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.log("Flow generation cancelled");
    } else {
      console.error("Error generating flow:", error);
    }
    throw error;
  }
}

/**
 * React hook for generating flow using TanStack Query with abort capability
 * @param onSettled - Optional callback when mutation settles (success or error)
 * @returns UseGenerateFlowServiceResult with mutate and abort functions
 */
export default function useGenerateFlowService(
  onSettled?: (res: Chat | undefined, error: Error | null) => void
): UseGenerateFlowServiceResult {
  const { getAuthHeaders } = useAuthHeaders();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: GenerateFlowPayload) => {
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return generateFlow(
        { ...payload, token },
        abortControllerRef.current.signal
      );
    },

    onSettled: (data, error) => {
      if (onSettled) {
        onSettled(data ?? undefined, error instanceof Error ? error : null);
      }
      abortControllerRef.current = null;
    },
  });

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    ...mutation,
    abort,
  };
}
